import React from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/Dialog';
import { cn } from '@repo/ui/utils';

interface BaseDialogProps {
  title: string;
  description?: React.ReactNode;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showOverlay?: boolean;
  hideCloseButton?: boolean;
  preventOutsideClose?: boolean;
  /**
   * Turns the dialog body into a fixed-height flex column instead of letting
   * the whole dialog scroll: the header stays put, and it's up to `children`
   * to mark its own scrollable region (e.g. `flex-1 min-h-0 overflow-y-auto`)
   * so a footer rendered after it stays fixed in place.
   */
  scrollableBody?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function BaseDialog({
  title,
  description,
  trigger,
  open,
  onOpenChange,
  showOverlay = false,
  hideCloseButton,
  preventOutsideClose,
  scrollableBody,
  className,
  children,
}: BaseDialogProps) {
  const isComplexDescription =
    description !== undefined && typeof description !== 'string';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        showOverlay={showOverlay}
        hideCloseButton={hideCloseButton}
        className={cn(
          scrollableBody && 'flex flex-col overflow-y-hidden',
          className
        )}
        onPointerDownOutside={
          preventOutsideClose ? (event) => event.preventDefault() : undefined
        }
        onEscapeKeyDown={
          preventOutsideClose ? (event) => event.preventDefault() : undefined
        }
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription
            asChild={isComplexDescription}
            className={cn(!description && 'sr-only')}
          >
            {isComplexDescription ? (
              <div>{description}</div>
            ) : (
              ((description as string | undefined) ?? title)
            )}
          </DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export { DialogClose, DialogFooter };
